<script setup>
import { nextTick, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { useDropdownPosition } from 'dashboard/composables/useDropdownPosition';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

// Menu "⋯" usado nas colunas e nos cards.
// Precisa de teleport + posição fixa porque o board tem overflow-x-auto
// (um dropdown absolute seria cortado).
defineProps({
  menuItems: { type: Array, required: true },
  triggerLabel: { type: String, default: '' },
  triggerClass: { type: String, default: '' },
});

const emit = defineEmits(['action']);

const isOpen = ref(false);
const triggerRef = ref(null);
const popoverRef = ref(null);

const { fixedPosition, updatePosition } = useDropdownPosition(
  triggerRef,
  popoverRef,
  isOpen,
  { align: 'end' }
);

const openMenu = async () => {
  isOpen.value = true;
  await nextTick();
  updatePosition();
};

const closeMenu = () => {
  isOpen.value = false;
};

const handleClickOutside = event => {
  if (triggerRef.value?.contains(event.target)) return;
  closeMenu();
};

const handleAction = payload => {
  emit('action', payload);
  closeMenu();
};
</script>

<template>
  <div ref="triggerRef" class="relative shrink-0" @click.stop>
    <Button
      ghost
      slate
      xs
      icon="i-lucide-more-horizontal"
      :title="triggerLabel"
      :aria-label="triggerLabel"
      class="!size-6 !text-n-slate-11 hover:!text-n-slate-12"
      :class="[triggerClass, { '!bg-n-alpha-2': isOpen }]"
      @click.stop.prevent="isOpen ? closeMenu() : openMenu()"
    />
    <TeleportWithDirection>
      <DropdownMenu
        v-if="isOpen"
        ref="popoverRef"
        v-on-click-outside="handleClickOutside"
        data-popover-content
        :menu-items="menuItems"
        :class="fixedPosition.class"
        :style="fixedPosition.style"
        class="w-52 !fixed"
        @action="handleAction"
      />
    </TeleportWithDirection>
  </div>
</template>
